import { slugify } from '../utils/slugify';

// src/utils/imageUtils.ts

const getResizedDimensions = (
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } => {
  let nextWidth = width;
  let nextHeight = height;

  if (nextWidth > maxWidth || nextHeight > maxHeight) {
    if (nextWidth > nextHeight) {
      nextHeight = Math.round((nextHeight * maxWidth) / nextWidth);
      nextWidth = maxWidth;
    } else {
      nextWidth = Math.round((nextWidth * maxHeight) / nextHeight);
      nextHeight = maxHeight;
    }
  }

  return {
    width: nextWidth,
    height: nextHeight,
  };
};

const getOutputFormat = (imageFile: File): string =>
  imageFile.type.startsWith('image/png') ? 'image/png' : 'image/webp';

const buildResizedFileName = (imageFile: File): string => {
  const originalFileNameParts = imageFile.name.split('.');
  const extension = originalFileNameParts.pop();
  const baseName = originalFileNameParts.join('.');

  return `${slugify(baseName)}.${extension}`;
};

/**
 * Resizes an image file if it exceeds specified dimensions, maintaining aspect ratio.
 *
 * @param imageFile The original image File object.
 * @param maxWidth The maximum width for the resized image.
 * @param maxHeight The maximum height for the resized image.
 * @param quality The image quality (0 to 1) for JPEG/WEBP compression.
 * @returns A Promise that resolves with a new File object (if resized) or the original File object (if no resizing needed or on error).
 */
export const resizeImage = (
  imageFile: File,
  maxWidth: number,
  maxHeight: number,
  quality: number = 0.8
): Promise<File> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(imageFile);
    reader.onload = (event: ProgressEvent<FileReader>) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const { width, height } = getResizedDimensions(
          img.width,
          img.height,
          maxWidth,
          maxHeight
        );

        // Only resize if dimensions have changed
        if (width === img.width && height === img.height) {
          resolve(imageFile); // No resizing needed, return original file
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);

          const outputFormat = getOutputFormat(imageFile);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedFile = new File([blob], buildResizedFileName(imageFile), {
                  type: outputFormat,
                  lastModified: Date.now(),
                });
                resolve(resizedFile);
              } else {
                resolve(imageFile); // Fallback to original file if blob creation fails
              }
            },
            outputFormat,
            quality
          );
        } else {
          resolve(imageFile); // Fallback to original file if canvas context fails
        }
      };
      img.onerror = () => {
        console.error("Error loading image for resizing.");
        resolve(imageFile); // Fallback to original file on image load error
      };
    };
    reader.onerror = () => {
      console.error("Error reading image file.");
      resolve(imageFile); // Fallback to original file on file read error
    };
  });
};
